import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationService, type MemoryNotification } from '@/services/notificationService';

const categoryLabel: Record<string, string> = {
  last_year: '작년 오늘',
  past_today: '과거의 오늘',
  last_month: '지난달 오늘',
  photo_day: '사진이 있는 날',
};

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<MemoryNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  const refresh = async () => {
    const result = await notificationService.list();
    setItems(result.notifications);

    if ('setAppBadge' in navigator) {
      try {
        await (navigator as Navigator & {
          setAppBadge: (count?: number) => Promise<void>;
          clearAppBadge?: () => Promise<void>;
        }).setAppBadge(result.unreadCount);
      } catch {
        // 배지 API를 지원하지 않는 기기나 런처에서는 무시합니다.
      }
    }
  };

  useEffect(() => {
    refresh()
      .catch(() => setMessage('받은 알림을 불러오지 못했습니다.'))
      .finally(() => setLoading(false));
  }, []);

  const openItem = async (item: MemoryNotification) => {
    if (!item.isRead) {
      await notificationService.markRead(item.id).catch(() => undefined);
    }
    navigate(`/memories/${item.id}`);
  };

  const markAllRead = async () => {
    setMessage('');
    try {
      await notificationService.markAllRead();
      await refresh();
      setMessage('모든 알림을 읽음으로 표시했습니다.');
    } catch {
      setMessage('알림 상태를 변경하지 못했습니다.');
    }
  };

  return (
    <div className="min-h-screen bg-background-50 pb-28">
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-background-200 bg-background-50/95 px-5 py-4 backdrop-blur">
        <div>
          <h1 className="text-xl font-bold text-foreground-900">알림</h1>
          <p className="mt-0.5 text-xs text-foreground-400">최근 7일 동안 받은 가족 추억입니다</p>
        </div>
        {items.length > 0 && (
          <button
            type="button"
            onClick={markAllRead}
            className="text-sm font-semibold text-foreground-500"
          >
            모두 읽음
          </button>
        )}
      </header>

      <main className="space-y-4 p-4">
        {message && (
          <p className="rounded-2xl bg-accent-50 px-4 py-3 text-center text-sm text-accent-700">
            {message}
          </p>
        )}

        {loading ? (
          <div className="py-16 text-center text-foreground-400">불러오는 중...</div>
        ) : items.length === 0 ? (
          <div className="rounded-3xl border border-background-200 bg-white p-10 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary-100 text-foreground-400">
              <i className="ri-notification-off-line text-2xl" />
            </div>
            <h2 className="mt-4 font-bold text-foreground-900">아직 받은 알림이 없습니다</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-500">
              설정에서 추억 알림을 켜면 사진이 있는 날에 하루 한 번 알려드려요.
            </p>
            <button
              type="button"
              onClick={() => navigate('/settings/notifications')}
              className="mt-5 rounded-2xl bg-foreground-900 px-5 py-3 text-sm font-semibold text-white"
            >
              알림 설정 열기
            </button>
          </div>
        ) : (
          items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => openItem(item)}
              className="relative w-full overflow-hidden rounded-3xl border border-background-200 bg-white text-left transition-transform active:scale-[0.99]"
            >
              {!item.isRead && (
                <span className="absolute right-4 top-4 z-10 h-2.5 w-2.5 rounded-full bg-accent-500" />
              )}
              {item.imageUrl && (
                <img
                  src={item.imageUrl}
                  alt="추억 대표 사진"
                  className="aspect-[16/10] w-full object-cover"
                />
              )}
              <div className="p-4">
                <span className="text-xs font-semibold text-accent-600">
                  {categoryLabel[item.category]}
                </span>
                <h2 className="mt-1 text-lg font-bold text-foreground-900">{item.title}</h2>
                <p className="mt-1 text-sm text-foreground-500">{item.body}</p>
                <div className="mt-3 flex items-center justify-between gap-3 text-xs text-foreground-400">
                  <span>{item.memoryDate} · 사진 {item.photoCount}장</span>
                  <span className="inline-flex items-center gap-1 font-semibold text-accent-600">
                    사진 보기
                    <i className="ri-arrow-right-s-line" />
                  </span>
                </div>
              </div>
            </button>
          ))
        )}
      </main>
    </div>
  );
}
